import {
  SETTINGS_PHONE,
  sanitizePhoneNumber,
  validatePhoneNumber,
} from '@team-milva/utils/phone'
import type { PhoneNumberMeta } from '@team-milva/utils/phone'

/**
 * Bulk import of business customer users from a CSV file (MIL-392).
 *
 * Pure and free of Vue/store dependencies so the validation rules are testable
 * on their own. The rules mirror the single-create form
 * (`CreateCustomerUserBusinessModal.vue`): first name, last name, email and
 * phone are required. There is no role column — every imported user starts as
 * `user`, and a role column left over in an older file is simply ignored.
 *
 * E-mail addresses are lower-cased here, so the preview shows what will be created
 * and the duplicate check needs no case handling of its own.
 *
 * `lab-cloud` re-validates every row server-side; this exists so the user sees
 * what is wrong with their file before anything is sent, not as the only check.
 */

/** Must stay in step with `IMPORT_CUSTOMER_USERS_MAX_ROWS` in lab-cloud. */
export const CUSTOMER_USER_IMPORT_MAX_ROWS = 200

/**
 * The countries the phone utilities actually know about. `SETTINGS_PHONE` is
 * keyed by UPPERCASE ISO2, while the model stores the code lowercase — so a
 * country cell is validated uppercased and stored lowercased.
 *
 * This is a genuinely short list (DK, NO, FR, GB, DE at the time of writing).
 * A user typing any other country code gets `country-unsupported` naming the
 * ones that work, rather than a vague rejection.
 */
export const SUPPORTED_IMPORT_COUNTRIES: string[] = Object.keys(SETTINGS_PHONE)
  .map((code) => code.toLowerCase())
  .sort()

/**
 * The template is written with `;` because the people filling it in open it in
 * Danish Excel, which uses `;` as its list separator and would otherwise put
 * every row in a single column. Parsing accepts `,` just as happily — a file
 * that came back through a different tool still works.
 */
const TEMPLATE_SEPARATOR = ';'

/**
 * A UTF-8 BOM. Without it Excel reads the file as the local ANSI codepage and
 * turns `æøå` into mojibake on the round trip.
 */
const BOM = '﻿'

type TColumnKey = 'firstName' | 'lastName' | 'email' | 'iso2' | 'phone'

const COLUMNS: { key: TColumnKey; header: string; required: boolean }[] = [
  { key: 'firstName', header: 'Fornavn', required: true },
  { key: 'lastName', header: 'Efternavn', required: true },
  { key: 'email', header: 'E-mail', required: true },
  { key: 'iso2', header: 'Land (ISO2)', required: true },
  { key: 'phone', header: 'Telefonnummer', required: true },
]

export type TCustomerUserImportRow = {
  rowNumber: number
  firstName: string
  lastName: string
  email: string
  phone: PhoneNumberMeta
}

export type TCustomerUserImportRejectedRow = {
  rowNumber: number
  /** The cells exactly as the file had them (trimmed), so the preview can show what was sent in. */
  firstName: string
  lastName: string
  email: string
  country: string
  phone: string
  reason: string
}

export type TCustomerUserImportParseResult =
  | {
      /** The file is unusable as a whole — nothing can be imported from it. */
      ok: false
      error: string
    }
  | {
      ok: true
      rows: TCustomerUserImportRow[]
      invalidRows: TCustomerUserImportRejectedRow[]
      duplicateRows: TCustomerUserImportRejectedRow[]
    }

/** Builds the downloadable CSV template, BOM included. */
export function buildCustomerUserImportTemplate(): string {
  const header = COLUMNS.map((c) => c.header).join(TEMPLATE_SEPARATOR)
  const example = ['Jens', 'Jensen', 'jens@eksempel.dk', 'dk', '12345678'].join(
    TEMPLATE_SEPARATOR
  )

  return `${BOM}${header}\r\n${example}\r\n`
}

/** Splits one CSV line, honouring quoted values that contain the separator. */
function splitLine(line: string, separator: string): string[] {
  const values: string[] = []
  let current = ''
  let inQuotes = false

  for (let i = 0; i < line.length; i++) {
    const char = line[i]

    if (char === '"') {
      // A doubled quote inside a quoted value is a literal quote.
      if (inQuotes && line[i + 1] === '"') {
        current += '"'
        i++
        continue
      }
      inQuotes = !inQuotes
      continue
    }

    if (char === separator && !inQuotes) {
      values.push(current)
      current = ''
      continue
    }

    current += char
  }

  values.push(current)

  return values.map((v) => v.trim())
}

/**
 * Picks the separator by counting both candidates in the header line. Whichever
 * splits the header into more columns wins — a header with no separator at all
 * falls through to the wrong-columns error below.
 */
function detectSeparator(headerLine: string): string {
  const semicolons = splitLine(headerLine, ';').length
  const commas = splitLine(headerLine, ',').length

  return semicolons >= commas ? ';' : ','
}

function normalizeHeader(value: string): string {
  return value.replace(BOM, '').trim().toLowerCase()
}

/**
 * A number pasted with its country code ("+45 12 34 56 78", "0045 12345678") keeps just the
 * national part. `validatePhoneNumber` expects the number without the dial code, so leaving it
 * on turned a perfectly good number into "Telefonnummer er ikke gyldigt".
 *
 * Only an explicit international marker (`+` or `00`) is treated as a country code, and only when
 * it matches the row's own country — a national DK number may legitimately start with 45, and
 * "+46 …" on a Danish row is a real mismatch that should still be rejected.
 */
function stripDialCode(raw: string, iso2: string): string {
  const dialCode = SETTINGS_PHONE[iso2.toUpperCase()]?.dialCode
  const trimmed = raw.trim()

  if (!dialCode || !/^(\+|00)/.test(trimmed)) {
    return raw
  }

  const digits = trimmed.replace(/\D/g, '')
  // "+" leaves no marker in the digits; "00" leaves two leading zeros to drop.
  const withoutMarker = trimmed.startsWith('+') ? digits : digits.slice(2)
  const prefix = String(dialCode)

  return withoutMarker.startsWith(prefix)
    ? withoutMarker.slice(prefix.length)
    : raw
}

export function parseCustomerUserImportCsv(
  text: string
): TCustomerUserImportParseResult {
  const withoutBom = text.startsWith(BOM) ? text.slice(BOM.length) : text

  const lines = withoutBom
    .split(/\r\n|\n|\r/)
    .filter((line) => line.trim().length > 0)

  const headerLine = lines[0]

  if (!headerLine) {
    return { ok: false, error: 'empty-file' }
  }

  const separator = detectSeparator(headerLine)
  const headers = splitLine(headerLine, separator).map(normalizeHeader)

  const columnIndexes = {} as Record<TColumnKey, number>
  const missingHeaders: string[] = []

  COLUMNS.forEach((column) => {
    const index = headers.indexOf(normalizeHeader(column.header))

    if (index === -1 && column.required) {
      missingHeaders.push(column.header)
    }

    columnIndexes[column.key] = index
  })

  if (missingHeaders.length > 0) {
    return { ok: false, error: `missing-columns:${missingHeaders.join(', ')}` }
  }

  const dataLines = lines.slice(1)

  if (dataLines.length === 0) {
    return { ok: false, error: 'no-data-rows' }
  }

  if (dataLines.length > CUSTOMER_USER_IMPORT_MAX_ROWS) {
    return { ok: false, error: `too-many-rows:${dataLines.length}` }
  }

  const rows: TCustomerUserImportRow[] = []
  const invalidRows: TCustomerUserImportRejectedRow[] = []
  const duplicateRows: TCustomerUserImportRejectedRow[] = []
  const seenEmails = new Set<string>()

  dataLines.forEach((line, index) => {
    const rowNumber = index + 1
    const values = splitLine(line, separator)
    const read = (key: TColumnKey): string => {
      const at = columnIndexes[key]
      return at === -1 ? '' : (values[at] ?? '')
    }

    const firstName = read('firstName')
    const lastName = read('lastName')
    // Addresses are stored and compared in lower case, so the preview shows what will be created.
    const email = read('email').toLowerCase()
    const iso2 = read('iso2').toLowerCase()
    const phoneRaw = sanitizePhoneNumber(stripDialCode(read('phone'), iso2))

    const rejected = {
      rowNumber,
      firstName,
      lastName,
      email,
      country: read('iso2'),
      phone: read('phone'),
    }

    const reject = (reason: string): void => {
      invalidRows.push({ ...rejected, reason })
    }

    if (!firstName) return reject('first-name-missing')
    if (!lastName) return reject('last-name-missing')
    if (!email) return reject('email-missing')
    // Deliberately loose: the authoritative check is Firebase Auth's own, and a
    // stricter pattern here would reject addresses that are actually valid.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      return reject('email-invalid')
    if (!iso2) return reject('country-missing')
    if (!SETTINGS_PHONE[iso2.toUpperCase()])
      return reject('country-unsupported')
    if (!phoneRaw) return reject('phone-missing')
    if (!validatePhoneNumber(phoneRaw, iso2)) return reject('phone-invalid')

    const emailKey = email

    if (seenEmails.has(emailKey)) {
      duplicateRows.push({ ...rejected, reason: 'duplicate-in-file' })
      return
    }

    seenEmails.add(emailKey)

    rows.push({
      rowNumber,
      firstName,
      lastName,
      email,
      phone: { number: Number(phoneRaw), iso2 },
    })
  })

  return { ok: true, rows, invalidRows, duplicateRows }
}

/**
 * Moves rows whose e-mail already belongs to a user out of `rows`, so they are previewed as
 * skipped and never sent. The server still answers `already-exists` for anything this cannot see
 * (an address used by another lab's user), but the ones the lab can see are caught before submit.
 */
export function excludeExistingEmails(
  parsed: Extract<TCustomerUserImportParseResult, { ok: true }>,
  existingEmails: string[]
): Extract<TCustomerUserImportParseResult, { ok: true }> {
  const existing = new Set(
    existingEmails.map((email) => email.trim().toLowerCase())
  )
  const rows: TCustomerUserImportRow[] = []
  const alreadyExistingRows: TCustomerUserImportRejectedRow[] = []

  for (const row of parsed.rows) {
    if (!existing.has(row.email)) {
      rows.push(row)
      continue
    }

    alreadyExistingRows.push({
      rowNumber: row.rowNumber,
      firstName: row.firstName,
      lastName: row.lastName,
      email: row.email,
      country: row.phone.iso2,
      phone: String(row.phone.number),
      reason: 'already-exists',
    })
  }

  return {
    ...parsed,
    rows,
    invalidRows: [...parsed.invalidRows, ...alreadyExistingRows],
  }
}
